import React from 'react'
import { Button, ButtonShape, ButtonStyle } from '../../../components/simple/button'
import {
  ComposeState,
  ContactMethods,
  DEFAULT_COMPOSE_STATE,
  RecipientsInfo,
  SendOption,
  SendType,
  StudentGuardianRecipient,
  SingleStudentRecipient,
} from './composeTypes'
import { Teacher } from '../../../providers/context'
import { IconEmail, IconText, IconWarningCircle, IconWhatsapp } from '../../../icons/library'
import { LanguageGlobe, LanguageGlobeIcon } from '../messageTags'
import assert from 'assert'

const SinglePersonRecipientButton: React.FC<{
  individuals: RecipientsInfo['individuals']
  name: string
  studentID: number
  guardianID?: number
  setIndividuals: (individuals: RecipientsInfo['individuals']) => void
  limitRecipients?: boolean
  extraInvites: StudentGuardianRecipient[]
  setExtraInvites?: (extraInvites: StudentGuardianRecipient[]) => void
  sendTypes: Set<SendType>
  contactMethods: ContactMethods
  sendOption: SendOption
  globe?: LanguageGlobe
}> = ({
  individuals,
  name,
  studentID,
  guardianID,
  setIndividuals,
  limitRecipients,
  extraInvites,
  setExtraInvites,
  sendTypes,
  sendOption,
  contactMethods,
  globe,
}) => {
  let emailWarning: string | undefined = undefined
  let textWarning: string | undefined = undefined
  let whatsappWarning: string | undefined = undefined
  let generalWarning: string | undefined = undefined
  let disabled = false

  if (!sendTypes.has('mass')) {
    if (sendTypes.size === 1 && sendTypes.has('text')) {
      if (!contactMethods.text && contactMethods.email)
        emailWarning = 'This user does not have text so we will send them this message by email'
      if (contactMethods.whatsapp) whatsappWarning = 'This user will receive this message through WhatsApp'
    } else if (sendTypes.size === 1 && sendTypes.has('email')) {
      if (!contactMethods.email && contactMethods.text && !contactMethods.whatsapp)
        textWarning = 'This user does not have email so we will send them this message by text'
      if (!contactMethods.email && contactMethods.whatsapp)
        whatsappWarning = 'This user does not have email so we will send them this message through WhatsApp'
    } else if (sendTypes.has('email') && sendTypes.has('text') && contactMethods.whatsapp) {
      whatsappWarning = 'This user will receive this message through WhatsApp'
    }

    if (sendOption.type === 'call' && !contactMethods.phone) {
      disabled = true
    }

    if (
      (sendTypes.has('email') || sendTypes.has('text') || (sendTypes.has('phone') && sendOption.type !== 'call')) &&
      !contactMethods.email &&
      !contactMethods.text
    )
      generalWarning = contactMethods.elementary
        ? 'This user is an elementary student and does not have an email address.'
        : 'This user does not have any working contact methods to send this message through. They can only access it from our website.'
  }

  const isSelected = individuals.some((i) => i.studentID === studentID && i.guardianID === guardianID)

  React.useEffect(() => {
    if (isSelected && disabled)
      setIndividuals(individuals.filter((i) => i.studentID !== studentID || i.guardianID !== guardianID))
  }, [isSelected, disabled, studentID, guardianID, individuals])

  const addToExtraInvites = () => setExtraInvites!([...extraInvites, { studentID, guardianID }])
  const removeFromExtraInvites = () =>
    setExtraInvites!(extraInvites.filter((i) => i.studentID !== studentID || i.guardianID !== guardianID))
  const showExtraInviteSubbutton = setExtraInvites && !isSelected
  const button = (
    <Button
      shape={ButtonShape.roundedMedium}
      buttonStyle={isSelected ? (generalWarning ? ButtonStyle.warning : ButtonStyle.blueLight) : ButtonStyle.light}
      onClick={() => {
        setIndividuals(
          isSelected
            ? individuals.filter((i) => i.studentID !== studentID || i.guardianID !== guardianID)
            : [...(limitRecipients ? [] : individuals), { studentID, guardianID }]
        )
        // if we're limiting recipients, we need should add the previosuly selected individual to the extra invites because they were deselected
        if (!isSelected && limitRecipients && setExtraInvites && individuals.length > 0)
          setExtraInvites([...extraInvites, individuals[0]])
        // if we just got selectd, we should remove this individual from the extra invites
        if (setExtraInvites && isSelected) removeFromExtraInvites()
      }}
      className={`${showExtraInviteSubbutton ? 'gap-2 pb-4 leading-3' : ''}`}
      disabled={disabled}
    >
      {name}
      <div className="-mr-1 flex gap-2">
        {globe && <LanguageGlobeIcon {...globe} />}
        {emailWarning && <IconEmail tooltip={emailWarning} />}
        {textWarning && <IconText tooltip={textWarning} />}
        {whatsappWarning && <IconWhatsapp tooltip={whatsappWarning} />}
        {generalWarning && <IconWarningCircle tooltip={generalWarning} />}
      </div>
    </Button>
  )
  if (!showExtraInviteSubbutton) return button
  const isExtraInvite = extraInvites.some((i) => i.studentID === studentID && i.guardianID === guardianID)
  return (
    <div className="relative overflow-hidden rounded-full">
      {button}
      <div
        className={`absolute bottom-0 left-0 right-0 flex h-4 cursor-pointer items-center justify-center text-xs ${isExtraInvite ? 'bg-grayBlue hover:bg-grayBlue-600' : 'bg-gray-100 hover:bg-gray-200'}`}
        onClick={isExtraInvite ? removeFromExtraInvites : addToExtraInvites}
      >
        Invite To Call
      </div>
    </div>
  )
}

interface RecipientInfoSelectorProps {
  composeState: ComposeState
  setRecipientsInfo: (d: (info: RecipientsInfo) => RecipientsInfo) => void
  limitRecipients: boolean
  showExtraInviteSubbuttons: boolean
  user: Teacher
  highlightReasonsWeCantSend?: boolean
}

export const RecipientInfoSelector: React.FC<RecipientInfoSelectorProps> = ({
  composeState,
  setRecipientsInfo,
  limitRecipients,
  showExtraInviteSubbuttons,
  user,
  highlightReasonsWeCantSend,
}) => {
  const selectedRecipients = composeState.selectedRecipients
  const recipientsInfo = composeState.recipientsInfo
  const sendTypes = composeState.sendTypes
  const sendOption = composeState.sendOption

  const previousSelectedRecipients = React.useRef(selectedRecipients)

  React.useEffect(() => {
    // handle cases where we just switched between 0, 1, >1 recipients. We try to select the most applicable people in each case
    if (selectedRecipients.length === previousSelectedRecipients.current.length) return
    previousSelectedRecipients.current = selectedRecipients

    let guardiansPreviouslySelected =
      recipientsInfo.toGuardians || recipientsInfo.individuals.some((i) => i.guardianID !== undefined)
    let studentsPreviouslySelected =
      recipientsInfo.toStudents || recipientsInfo.individuals.some((i) => i.guardianID === undefined)
    if (!guardiansPreviouslySelected && !studentsPreviouslySelected && !recipientsInfo.toSharedTeachers) {
      // apply defaults if nothing was previously selected
      guardiansPreviouslySelected = user.defaultRecipientType === 'guardian'
      studentsPreviouslySelected = user.defaultRecipientType === 'student'
    }
    if (selectedRecipients.length === 0) {
      setRecipientsInfo((ri) => DEFAULT_COMPOSE_STATE.recipientsInfo)
    } else if (selectedRecipients.length === 1 && !selectedRecipients[0].group) {
      const student = selectedRecipients[0] as SingleStudentRecipient
      const possibleGuardians = limitRecipients ? student.guardians.slice(0, 1) : student.guardians
      setRecipientsInfo((ri) => ({
        ...ri,
        toStudents: false,
        toGuardians: false,
        individuals: (studentsPreviouslySelected ? [{ studentID: student.id }] : []).concat(
          guardiansPreviouslySelected
            ? possibleGuardians.map((g) => ({
                studentID: student.id,
                guardianID: g.id,
              }))
            : []
        ),
        extraInvites: [],
      }))
    } else {
      setRecipientsInfo((ri) => ({
        ...ri,
        toStudents: studentsPreviouslySelected,
        toGuardians: guardiansPreviouslySelected,
        individuals: [],
        extraInvites: [],
      }))
    }
  }, [selectedRecipients])

  React.useEffect(() => {
    if (sendOption.type === 'call')
      setRecipientsInfo((ri) => ({ ...ri, toSharedTeachers: false, sharedTeacherRecipients: [] }))
  }, [sendOption.type])

  React.useEffect(() => {
    if (!showExtraInviteSubbuttons) setRecipientsInfo((ri) => ({ ...ri, extraInvites: [] }))
  }, [showExtraInviteSubbuttons])

  React.useEffect(() => {
    if (limitRecipients && recipientsInfo.individuals.length > 1)
      setRecipientsInfo((ri) => ({ ...ri, individuals: [recipientsInfo.individuals[0]] }))
  }, [limitRecipients])

  assert(user.type === 'teacher', 'RecipientSelector should only be used by teachers')
  const singleRecipient =
    selectedRecipients.length === 1 && !selectedRecipients[0].group
      ? (selectedRecipients[0] as SingleStudentRecipient)
      : undefined

  if (selectedRecipients.length === 0) return null // not sure if we want this, different from our old behavior

  const borderClass = `rounded-[14.7px] border transition-colors duration-150 ${
    highlightReasonsWeCantSend &&
    composeState.recipientsInfo.individuals.length === 0 &&
    ((!composeState.recipientsInfo.toGuardians &&
      !composeState.recipientsInfo.toStudents &&
      !composeState.recipientsInfo.toSharedTeachers) ||
      composeState.sendOption.requiresIndividualRecipient)
      ? 'border-red-500'
      : 'border-transparent'
  }`

  return (
    <div
      className={`flex flex-wrap gap-2 ${borderClass} ${selectedRecipients.length === 0 ? 'pointer-events-none opacity-50' : ''}`}
      id="tutorial_recipientInfoSelector"
    >
      {singleRecipient ? (
        <>
          <SinglePersonRecipientButton
            individuals={recipientsInfo.individuals}
            name={singleRecipient.name}
            studentID={singleRecipient.id}
            limitRecipients={limitRecipients}
            setIndividuals={(individuals) => setRecipientsInfo((ri) => ({ ...ri, individuals }))}
            extraInvites={recipientsInfo.extraInvites}
            setExtraInvites={
              showExtraInviteSubbuttons
                ? (extraInvites) => setRecipientsInfo((ri) => ({ ...ri, extraInvites }))
                : undefined
            }
            sendTypes={sendTypes}
            contactMethods={singleRecipient.contactMethods}
            sendOption={sendOption}
            globe={singleRecipient.globe}
          />
          {singleRecipient.guardians.map((guardian) => (
            <SinglePersonRecipientButton
              key={guardian.id}
              individuals={recipientsInfo.individuals}
              name={guardian.name}
              studentID={singleRecipient.id}
              guardianID={guardian.id}
              limitRecipients={limitRecipients}
              setIndividuals={(individuals) => setRecipientsInfo((ri) => ({ ...ri, individuals }))}
              extraInvites={recipientsInfo.extraInvites}
              setExtraInvites={
                showExtraInviteSubbuttons
                  ? (extraInvites) => setRecipientsInfo((ri) => ({ ...ri, extraInvites }))
                  : undefined
              }
              sendTypes={sendTypes}
              contactMethods={guardian.contactMethods}
              sendOption={sendOption}
              globe={guardian.globe}
            />
          ))}
        </>
      ) : (
        <>
          <Button
            shape={ButtonShape.roundedMedium}
            buttonStyle={recipientsInfo.toStudents ? ButtonStyle.blueLight : ButtonStyle.light}
            onClick={() => setRecipientsInfo((ri) => ({ ...ri, toStudents: !ri.toStudents }))}
          >
            Students
          </Button>
          <Button
            shape={ButtonShape.roundedMedium}
            buttonStyle={recipientsInfo.toGuardians ? ButtonStyle.blueLight : ButtonStyle.light}
            onClick={() => setRecipientsInfo((ri) => ({ ...ri, toGuardians: !ri.toGuardians }))}
          >
            Guardians
          </Button>
        </>
      )}
      <Button
        shape={ButtonShape.roundedMedium}
        buttonStyle={recipientsInfo.toSharedTeachers ? ButtonStyle.blueLight : ButtonStyle.light}
        onClick={() =>
          setRecipientsInfo((ri) => ({
            ...ri,
            toSharedTeachers: !ri.toSharedTeachers,
            sharedTeacherRecipients: ri.toSharedTeachers ? [] : ri.sharedTeacherRecipients,
          }))
        }
        disabled={sendOption.type === 'call'}
      >
        Staff
      </Button>
    </div>
  )
}
